import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { GetReportsResponseDto } from "../../model/reports/dto/GetReportsResponseDto";
import { GetReportsDto } from "../../model/reports/dto/GetReportsDto";
import { ReportAxios } from "../../axios/report/report.axios";
import { revertAllReduxAction } from "../store-reset";

interface ReportReducer {
  report?: GetReportsResponseDto;
  reportLoading?: boolean;
  /**
   * The request whose answer this slice is still waiting for. Without it the
   * last response to *resolve* wins rather than the last one asked for, and a
   * slow earlier range can land on top of the one the user actually picked.
   */
  pendingRequestId?: string;
}

const initialState = Object.freeze<ReportReducer>({
  report: undefined,
  reportLoading: false,
  pendingRequestId: undefined,
});

const getReport = AtiraThunk<GetReportsResponseDto, GetReportsDto>(
  "report/getReport",
  ReportAxios.getReport,
);

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {},
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getReport.pending, (state, action) => {
      state.pendingRequestId = action.meta.requestId;
      state.reportLoading = true;
    });
    addCase(getReport.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.pendingRequestId) {
        return;
      }

      state.report = action.payload;
      state.pendingRequestId = undefined;
      state.reportLoading = false;
    });
    addCase(getReport.rejected, (state, action) => {
      if (action.meta.requestId !== state.pendingRequestId) {
        return;
      }

      // Drop the old payload rather than re-lighting it at full opacity: a
      // failed refetch that leaves the previous window on screen is
      // indistinguishable from a successful one.
      state.report = undefined;
      state.pendingRequestId = undefined;
      state.reportLoading = false;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const reportActions = {
  getReport,
};

export default reportSlice.reducer;
